/* eslint-disable no-bitwise */

import { type Ref, ref, watchEffect } from 'vue'

import type {
  TreeSelectModelSetter,
  TreeSelectOption,
  TreeSelectOptionValue,
  TreeSelectState,
} from '../TreeSelect.types'

/*
  TreeSelect is a component that allows the user to select multiple options
  in a tree structure. The options can be checked, unchecked, disabled or enabled.

  The state of the component is managed by a model, which is an array of values.

  The component has two main functions:
    - toggleOption: toggles the state of an option and its children
    - toggleAll: toggles the state of all options

  The component has a state that is an object with two properties:
    - all: a bit flag that indicates if all options are checked
    - options: an object with the state of each option

  The state of each option is a bit flag that indicates if the option is checked,
  has disabled children, has enabled children or is disabled itself.

  The component has some helper functions to check the state of an option:
    - isChecked: checks if an option is checked
    - isDisabled: checks if an option is disabled
    - hasChildren: checks if an option has children
    - hasContent: checks if an option has content

  The component has a watcher that updates the state when the model changes.
*/
export function useTreeSelect(
  model: Ref<TreeSelectOptionValue[]>,
  availableOptions: TreeSelectOption[],
) {
  // Bit flags
  // In the future we could use uint8 to lighten the memory
  // and/or use complements (~) as masks
  // Warning: a 'disabled' child can be also 'checked', but we ignore it
  const HAS_DISABLED_CHILDREN = 0b1000
  const HAS_ENABLED_CHILDREN = 0b0100
  const HAS_CHECKED_CHILDREN = 0b0010
  // This flag might be ignored with a right shift (>> 1)
  const IS_SELF_CHECKED = 0b0001

  const state = ref<TreeSelectState>()

  function hasChildren(option: TreeSelectOption) {
    return !!option.children?.length
  }

  function hasContent(option: TreeSelectOption) {
    return !!option.description || hasChildren(option)
  }

  function getOptionKey(option: TreeSelectOption) {
    return option.value || option.label
  }

  function isOptionStateChecked(optionState = 0) {
    // If it's checked itself or has only checked children
    return (
      !!(optionState & (IS_SELF_CHECKED | HAS_CHECKED_CHILDREN)) &&
      !(optionState & HAS_ENABLED_CHILDREN)
    )
  }

  function isOptionStateDisabled(optionState = 0) {
    // Shift right to ignore is checked itself
    // Everything needs to be disabled
    return !((optionState ^ HAS_DISABLED_CHILDREN) >> 1)
  }

  watchEffect(() => {
    state.value = availableOptions.reduce(
      (obj, option) => {
        let optionState
        // If it has children, we need to check if they are disabled or checked
        // The optionState is the OR of all children states
        if (hasChildren(option)) {
          optionState = (option.children || []).reduce(
            (oState, { value, disabled }) => {
              return disabled
                ? oState | HAS_DISABLED_CHILDREN
                : oState |
                    (model.value.includes(value)
                      ? HAS_CHECKED_CHILDREN
                      : HAS_ENABLED_CHILDREN)
            },
            0,
          )
        } else {
          // If it's a leaf, we need to check if it's disabled or checked
          // The optionState is the OR of its own states
          optionState =
            0 |
            (option.disabled || !option.value ? HAS_DISABLED_CHILDREN : 0) |
            (!!option.value && model.value.includes(option.value)
              ? IS_SELF_CHECKED
              : 0)
        }

        return {
          all:
            isOptionStateDisabled(optionState) ||
            isOptionStateChecked(optionState)
              ? obj.all
              : 0,
          options: {
            ...obj.options,
            [getOptionKey(option)]: optionState,
          },
        }
      },
      { all: IS_SELF_CHECKED, options: {} } as TreeSelectState,
    )
  })

  function isChecked(option: TreeSelectOption) {
    return isOptionStateChecked(state.value?.options[getOptionKey(option)])
  }

  function isDisabled(option: TreeSelectOption) {
    return isOptionStateDisabled(state.value?.options[getOptionKey(option)])
  }

  function isAllChecked() {
    return !!((state.value?.all || 0) & IS_SELF_CHECKED)
  }

  function toggleOption(option: TreeSelectOption): TreeSelectModelSetter {
    if (hasChildren(option)) {
      const children = option.children || []
      const values = children.reduce((arr, { value, disabled }) => {
        return disabled ? arr : [...arr, value]
      }, [] as TreeSelectOptionValue[])

      return { [isChecked(option) ? 'remove' : 'add']: values }
    }
    if (!option.disabled && !!option.value) {
      return { [isChecked(option) ? 'remove' : 'add']: [option.value] }
    }

    return {}
  }

  function toggleAll(): TreeSelectModelSetter {
    return {
      [isAllChecked() ? 'remove' : 'add']: availableOptions.reduce(
        (arr, option) => {
          if (hasChildren(option)) {
            return [
              ...arr,
              ...(option.children || []).reduce(
                (subarr, { value, disabled }) => {
                  return disabled ? subarr : [...subarr, value]
                },
                [] as TreeSelectOptionValue[],
              ),
            ]
          }
          if (!option.disabled && option.value) {
            return [...arr, option.value]
          }

          return arr
        },
        [] as TreeSelectOptionValue[],
      ),
    }
  }

  return {
    state,
    isAllChecked,
    isChecked,
    isDisabled,
    hasChildren,
    hasContent,
    toggleOption,
    toggleAll,
  }
}
